import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  addRentalDuration,
  formatRentalPeriod,
  RENTAL_DAY_OPTIONS,
  RENTAL_HOUR_OPTIONS,
} from '@/feature/device/utils/deviceLabels';
import FormSelect from '@/shared/components/forms/FormSelect';
import Button from '@/shared/components/ui/Button';
import Modal from '@/shared/components/ui/Modals/Modal';
import { formatDate } from '@/shared/utils/dateUtils';

interface IExtendRentalModalProps {
  isOpen: boolean;
  deviceName: string;
  orgName?: string;
  rentalStart?: string | null;
  rentalEnd?: string | null;
  onClose: () => void;
  onExtend: (data: { extension_days: number; extension_hours: number }) => Promise<boolean>;
}

interface IExtendForm {
  extension_days: string;
  extension_hours: string;
}

const compactField = '!rounded-md !py-2 !text-sm border-gray-300';

const formatPreviewDateTime = (date: Date): string => {
  const datePart = formatDate(date);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return datePart ? `${datePart} ${hours}:${minutes}` : '—';
};

const ExtendRentalModal = ({
  isOpen,
  deviceName,
  orgName,
  rentalStart,
  rentalEnd,
  onClose,
  onExtend,
}: IExtendRentalModalProps) => {
  const [durationError, setDurationError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<IExtendForm>({
    defaultValues: { extension_days: '1', extension_hours: '0' },
  });

  const extensionDays = watch('extension_days');
  const extensionHours = watch('extension_hours');
  const daysNum = Number(extensionDays || 0);
  const hoursNum = Number(extensionHours || 0);

  useEffect(() => {
    if (isOpen) {
      reset({ extension_days: '1', extension_hours: '0' });
      setDurationError(null);
    }
  }, [isOpen, reset]);

  const previewEnd = (() => {
    if (!rentalEnd) {
      return '—';
    }
    const end = new Date(rentalEnd);
    if (Number.isNaN(end.getTime())) {
      return formatDate(rentalEnd);
    }
    return formatPreviewDateTime(addRentalDuration(end, daysNum, hoursNum));
  })();

  const onValid = async (data: IExtendForm) => {
    const days = Number(data.extension_days);
    const hours = Number(data.extension_hours);
    if (days <= 0 && hours <= 0) {
      setDurationError('Select at least 1 day or 1 hour.');
      return;
    }
    setDurationError(null);
    const ok = await onExtend({ extension_days: days, extension_hours: hours });
    if (ok) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title='Extend Device Rental' size='md'>
      <form onSubmit={handleSubmit(onValid)} className='p-6'>
        <p className='text-sm text-gray-500 mb-4'>
          Device: <strong>{deviceName}</strong>
          {orgName ? (
            <>
              {' '}
              · Organization: <strong>{orgName}</strong>
            </>
          ) : null}
        </p>
        <div className='rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm mb-4'>
          <div className='flex justify-between'>
            <span className='text-gray-500'>Current Rental</span>
            <span className='font-semibold text-gray-900'>
              {formatDate(rentalStart) || '—'} – {formatDate(rentalEnd) || '—'}
            </span>
          </div>
        </div>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
          <FormSelect
            name='extension_days'
            label='Days'
            options={RENTAL_DAY_OPTIONS}
            register={register}
            errors={errors}
            value={extensionDays}
            onChange={(value) => {
              setDurationError(null);
              setValue('extension_days', String(value), { shouldValidate: true });
            }}
            validation={{ required: 'Days is required' }}
            className={compactField}
          />
          <FormSelect
            name='extension_hours'
            label='Hours'
            options={RENTAL_HOUR_OPTIONS}
            register={register}
            errors={errors}
            value={extensionHours}
            onChange={(value) => {
              setDurationError(null);
              setValue('extension_hours', String(value), { shouldValidate: true });
            }}
            validation={{ required: 'Hours is required' }}
            className={compactField}
          />
        </div>
        {durationError ? <p className='mt-2 text-sm text-red-600'>{durationError}</p> : null}
        <p className='mt-3 text-sm text-gray-600'>
          Extension: <strong>{formatRentalPeriod(daysNum, hoursNum)}</strong>
        </p>
        <p className='mt-1 text-sm text-gray-600'>
          New rental end: <strong>{previewEnd}</strong>
        </p>
        <div className='flex justify-end gap-3 mt-6'>
          <Button type='button' variant='secondary' onClick={onClose}>
            Cancel
          </Button>
          <Button type='submit' variant='primary' isLoading={isSubmitting}>
            Extend Rental
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ExtendRentalModal;
